import { useMemo, useState } from "react";
import { booking } from "../constants";

// --- Helpers date (FR, semaine commençant le lundi) ---
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Quelques créneaux marqués "complet" de façon déterministe → rend la démo crédible
const slotsForDate = (date) => {
  const seed = date.getDate();
  return booking.slots.map((time, i) => ({
    time,
    full: (seed + i) % 5 === 0, // ~1 créneau sur 5 affiché complet
  }));
};

const BookingCalendar = () => {
  const today = stripTime(new Date());
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + (booking.minLeadDays || 0));

  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", car: "", service: "Stage 1" });
  const [confirmed, setConfirmed] = useState(false);

  // Grille du mois affiché
  const cells = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // lundi = 0
    const arr = Array.from({ length: firstWeekday }, () => null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [view]);

  const canGoPrev =
    view.getFullYear() > today.getFullYear() ||
    (view.getFullYear() === today.getFullYear() && view.getMonth() > today.getMonth());

  const changeMonth = (delta) => {
    setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));
  };

  const isSelectable = (date) => {
    if (!date) return false;
    if (date < minDate) return false;
    if (booking.closedDays.includes(date.getDay())) return false;
    return true;
  };

  const pickDate = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const submit = (e) => {
    e.preventDefault();
    setConfirmed(true);
  };

  const reset = () => {
    setConfirmed(false);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: "", phone: "", car: "", service: "Stage 1" });
  };

  const longDate = (d) =>
    `${WEEKDAYS[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTHS[d.getMonth()].toLowerCase()} ${d.getFullYear()}`;

  // --- Écran de confirmation ---
  if (confirmed) {
    return (
      <div className="mt-12 rounded-2xl border border-white/10 bg-noir-800 p-8 sm:p-12 text-center">
        <div className="flex-center mx-auto w-16 h-16 rounded-full bg-ferrari/15 border border-ferrari/40 text-ferrari">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 13 4 4L19 7" />
          </svg>
        </div>
        <h3 className="mt-6 font-display font-bold text-2xl">Demande de rendez-vous envoyée !</h3>
        <p className="mt-3 text-chrome-dark">
          Récapitulatif pour <span className="text-white">{form.name || "votre rendez-vous"}</span> :
        </p>
        <div className="mt-5 inline-flex flex-col gap-1 text-sm text-chrome-light">
          <span><span className="text-chrome-dark">Prestation :</span> {form.service}</span>
          <span><span className="text-chrome-dark">Date :</span> {longDate(selectedDate)}</span>
          <span><span className="text-chrome-dark">Heure :</span> {selectedSlot}</span>
        </div>
        <p className="mt-6 text-xs text-chrome-dark">
          Vous recevrez une confirmation par téléphone. À très vite !
        </p>
        <button onClick={reset} className="btn-secondary mt-8">
          Prendre un autre rendez-vous
        </button>
      </div>
    );
  }

  return (
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Calendrier */}
      <div className="rounded-2xl border border-white/10 bg-noir-800 p-6">
        <div className="flex items-center justify-between mb-5">
          <button
            onClick={() => changeMonth(-1)}
            disabled={!canGoPrev}
            className="flex-center w-9 h-9 rounded-full border border-white/10 text-chrome-light enabled:hover:border-ferrari/60 disabled:opacity-30 transition-colors"
            aria-label="Mois précédent"
          >
            ‹
          </button>
          <span className="font-display font-semibold">
            {MONTHS[view.getMonth()]} {view.getFullYear()}
          </span>
          <button
            onClick={() => changeMonth(1)}
            className="flex-center w-9 h-9 rounded-full border border-white/10 text-chrome-light hover:border-ferrari/60 transition-colors"
            aria-label="Mois suivant"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs text-chrome-dark mb-2">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((date, i) => {
            if (!date) return <span key={`e${i}`} />;
            const selectable = isSelectable(date);
            const isSelected =
              selectedDate && stripTime(date).getTime() === stripTime(selectedDate).getTime();
            return (
              <button
                key={date.toISOString()}
                disabled={!selectable}
                onClick={() => pickDate(date)}
                className={`aspect-square rounded-lg text-sm transition-all
                  ${isSelected ? "bg-ferrari text-white font-semibold" : ""}
                  ${!isSelected && selectable ? "text-chrome-light hover:bg-white/10" : ""}
                  ${!selectable ? "text-chrome-dark/30 cursor-not-allowed line-through" : ""}`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Créneaux + formulaire */}
      <div className="rounded-2xl border border-white/10 bg-noir-800 p-6">
        {!selectedDate ? (
          <div className="h-full flex-center flex-col text-center text-chrome-dark py-12">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="mb-4">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <p className="text-sm">Sélectionnez une date pour voir les créneaux disponibles.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-chrome-dark">Créneaux du</p>
            <p className="font-display font-semibold capitalize mb-4">{longDate(selectedDate)}</p>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
              {slotsForDate(selectedDate).map(({ time, full }) => (
                <button
                  key={time}
                  disabled={full}
                  onClick={() => setSelectedSlot(time)}
                  className={`py-2 rounded-lg text-sm border transition-all
                    ${selectedSlot === time ? "bg-ferrari border-ferrari text-white font-semibold" : ""}
                    ${selectedSlot !== time && !full ? "border-white/15 text-chrome-light hover:border-ferrari/60" : ""}
                    ${full ? "border-white/5 text-chrome-dark/40 cursor-not-allowed" : ""}`}
                >
                  {full ? "complet" : time}
                </button>
              ))}
            </div>

            {selectedSlot && (
              <form onSubmit={submit} className="space-y-3 border-t border-white/10 pt-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    required
                    placeholder="Votre nom"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-noir-700 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-ferrari/60 transition-colors"
                  />
                  <input
                    required
                    type="tel"
                    placeholder="Téléphone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-noir-700 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-ferrari/60 transition-colors"
                  />
                </div>
                <input
                  placeholder="Véhicule (ex : Golf 7 GTD)"
                  value={form.car}
                  onChange={(e) => setForm({ ...form, car: e.target.value })}
                  className="w-full bg-noir-700 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-ferrari/60 transition-colors"
                />
                <select
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className="w-full bg-noir-700 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-ferrari/60 transition-colors"
                >
                  {["Stage 1", "Stage 2", "Flex Fuel E85", "FAP / EGR Off", "Stop & Start Off", "Décatalyseur", "Autre / Conseil"].map((s) => (
                    <option key={s} value={s} className="bg-noir-700">{s}</option>
                  ))}
                </select>
                <button type="submit" className="btn-primary w-full mt-1">
                  Confirmer le rendez-vous
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingCalendar;
